import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CompaniesService } from '../../../services/companies.service';
import {
  BillingCharge,
  BillingTypes,
  MasterPayrollObject,
  PayTypes
} from '../../../models/payroll';
import { PayrollService } from '../../../services/payroll.service';
import { AuditService } from '../../../services/audit.service';
import { PayrollInfo } from '../../../models/payroll-info';
import { concatMap, tap } from 'rxjs/operators';

@Component({
  selector: 'billing-select-modal',
  templateUrl: './billing-select-modal.component.html',
  standalone: false
})
export class BillingSelectModalComponent implements OnInit {
  @Input() auditKey: string;
  @Input() onClose: any;
  @Input() onDismiss: any;
  @Input() public payroll: MasterPayrollObject;

  billingCharges: BillingCharge[];
  payrollInfo: PayrollInfo;
  payrollType: string;
  selBillingType: string;
  sendNotification: boolean;

  get billingTypes() {
    return BillingTypes;
  }

  get payTypes() {
    return PayTypes;
  }

  billingOrder = {
    ADJUST: 0,
    BASE: 1,
    NONE: 2
  };

  constructor(
    public activeModal: NgbActiveModal,
    public companyService: CompaniesService,
    public payrollService: PayrollService,
    public auditService: AuditService
  ) {}

  ngOnInit(): void {
    if (this.payroll.PayType == PayTypes.OffCycle) {
      this.payrollType = 'off-cycle payroll';
    } else if (this.payroll.PayType == PayTypes.Adjustment) {
      this.payrollType = 'corrections';
    }

    // fetch PRI record. if BillingType property is not null, select that billing type in the modal
    this.payrollService
      .getPayrollInfo(this.payroll.ClientCode, this.payroll.Year, this.payroll.SequenceNumber)
      .pipe(
        concatMap(info => {
          this.payrollInfo = info;

          return this.companyService.getClientBillings(this.payroll.ClientCode).pipe(
            tap(res => {
              res.push(new BillingCharge('NONE', 0));

              // order by Adjustment, Payroll, No Charge
              res.sort((a, b) => {
                return this.billingOrder[a.BillingType] > this.billingOrder[b.BillingType] ? 1 : -1;
              });

              this.billingCharges = res;

              // if an option was selected and set in PayrollInfo, use that
              // else, pre select the first option
              this.selBillingType = info.BillingType ?? res[0].BillingType;

              // if the user checked the checkbox previously (info.SuppressNotifications === false)
              // sendNotification needs to be unchecked by default (info.SuppressNotifications === null)
              this.sendNotification = info.SuppressNotifications === false ? true : false;
            })
          );
        })
      )
      .subscribe();
  }

  public save() {
    const charge = this.billingCharges.find(x => x.BillingType == this.selBillingType);

    this.payrollService
      .updateBillingCharges(
        this.payroll.ClientCode,
        this.payroll.Year,
        this.payroll.SequenceNumber,
        {
          BillingCharge: charge,
          SuppressNotifications: !this.sendNotification
        }
      )
      .subscribe(() => {
        this.auditService.auditPayroll(
          this.auditKey,
          {
            ClientCode: this.payroll.ClientCode,
            SequenceNumber: this.payroll.SequenceNumber,
            Calendars: this.payroll.Calendars,
            LocationId: this.payroll.LocationId,
            PayDate: this.payroll.PayDate,
            PayrollType: this.payroll.PayType
          },
          {
            BillingCharge: this.billingTypes[charge.BillingType]
          }
        );

        this.auditService.auditPayroll(
          'payroll-notification-suppression-selected',
          {
            ClientCode: this.payroll.ClientCode,
            SequenceNumber: this.payroll.SequenceNumber,
            Calendars: this.payroll.Calendars,
            LocationId: this.payroll.LocationId,
            PayDate: this.payroll.PayDate,
            PayrollType: this.payroll.PayType
          },
          {
            SuppressNotifications: !this.sendNotification
          }
        );

        this.onClose();
      });
  }
}
